import {
  ResourceSearchItem,
  RESOURCE_TYPE,
  RESOURCE_TYPE_SINGULAR,
  SearchSuggestion,
  SearchUsersGroupsListItem,
} from 'interfaces'
import {getEndpointBaseUrl, instance} from 'services/api'
import {EndPoints} from 'services/api/HTTP/HTTP.types'

export const getSearchSuggestions = async (
  searchTerm: string,
): Promise<SearchSuggestionResult> => {
  const res = await Promise.all([
    fetchUserOrGroupSuggestions(searchTerm),
    fetchLocationSuggestions(searchTerm),
  ])

  return Promise.resolve({
    ...res[0],
    ...res[1],
  })
}

const fetchUserOrGroupSuggestions = async (
  searchTerm: string,
): Promise<SearchSuggestionResult> => {
  const requestPayload = {
    models: [RESOURCE_TYPE.Groups, RESOURCE_TYPE.Users],
    text: searchTerm,
  }
  try {
    const res = await instance.post(EndPoints.SearchUsersGroups, requestPayload)
    const {Users = [], Groups = []} = res?.data?.Items || {}
    const userResult = parseUsersGroupsSuggestion(
      Users,
      searchTerm,
      RESOURCE_TYPE.Users,
    )
    const groupResult = parseUsersGroupsSuggestion(
      Groups,
      searchTerm,
      RESOURCE_TYPE.Groups,
    )
    const result: SearchSuggestionResult = {}
    if (userResult.length) {
      result.user = userResult
    }
    if (groupResult.length) {
      result.group = groupResult
    }
    return result
  } catch (_) {
    // return empty result when API fails
    return {}
  }
}

const fetchLocationSuggestions = async (
  searchTerm: string,
): Promise<SearchSuggestionResult> => {
  const params = {
    prefix: searchTerm,
    type: RESOURCE_TYPE.Location,
  }
  try {
    const endpointBaseUrl = getEndpointBaseUrl()
    const res = await instance.get(
      `${endpointBaseUrl}${EndPoints.ResourceSearch}`,
      {params},
    )
    const suggestionsData: ResourceSearchItem[] = res?.data || []
    const locationSuggestion = suggestionsData.map(item => ({
      displayValue: item.display_term,
      searchTerm: item.search_term,
      resourceType: item?.type?.toLowerCase() as RESOURCE_TYPE,
      resourceTypeSingular: RESOURCE_TYPE_SINGULAR.Location,
      city: item.city,
      country: item.country,
    }))
    const hasSuggestion = locationSuggestion.length > 0
    return hasSuggestion ? {location: locationSuggestion} : {}
  } catch (_) {
    // return empty result when API fails
    return {}
  }
}

export const parseUsersGroupsSuggestion = (
  items: SearchUsersGroupsListItem[],
  searchTerm: string,
  resourceType: RESOURCE_TYPE,
): SearchSuggestion[] => {
  return (items || []).map(item => ({
    displayValue: `${item.name} (${item.emailId})`,
    searchTerm,
    resourceType,
    name: item?.name,
    emailId: item?.emailId,
  }))
}

export interface SearchSuggestionResult {
  user?: SearchSuggestion[]
  group?: SearchSuggestion[]
  location?: SearchSuggestion[]
}
